import React from 'react';
import { Form, Badge, Radio, Tooltip, Modal } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faPen, faMobileScreen, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { CheckOutlined } from '@ant-design/icons';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  PreferenceRow,
  PreferenceInfo,
  PreferenceLabel,
  PreferenceDescription,
  FormSection,
  SaveChangesButton,
  CancelButton,
  DisableButton,
  ActionButtons,
  StyledSwitch,
  ActionIconButton,
  StyledInput,
  TwoStepMethodOption,
  TwoStepInputWrap,
  TwoStepEnableBlock,
  TwoStepEnableTitle,
  TwoStepEnableDescription,
  TwoStepSwitchWrap,
} from './Styles';
import type { TwoStepVerificationSectionProps, TwoFactorMethod } from './types';
import { maskValue, formatExpiresAt } from './constants';

const TwoStepVerificationSection: React.FC<TwoStepVerificationSectionProps> = ({
  editingSection,
  formData,
  profileLoading,
  setupLoading,
  twoFactorCode,
  twoFactorSetupInProgress,
  expiresAt,
  onEdit,
  onSwitchChange,
  onInputChange,
  onMethodChange,
  onCancel,
  onSetupTwoFactor,
  onTwoFactorCodeChange,
  onConfirmTwoFactor,
  onResendTwoFactor,
  onDisableTwoFactor,
}) => {
  const [displayTime, setDisplayTime] = React.useState('');
  const [prevMethod, setPrevMethod] = React.useState(formData.twoFactorMethod);
  const [prevContact, setPrevContact] = React.useState(
    formData.twoFactorMethod === 'phone' ? formData.twoFactorPhone : formData.twoFactorEmail
  );
  const [showDisableConfirmModal, setShowDisableConfirmModal] = React.useState(false);

  const hasContactChanged = React.useMemo(() => {
    if (!twoFactorSetupInProgress) return false;

    const currentContact = formData.twoFactorMethod === 'phone'
      ? formData.twoFactorPhone
      : formData.twoFactorEmail;

    return prevMethod !== formData.twoFactorMethod || prevContact !== currentContact;
  }, [twoFactorSetupInProgress, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail, prevMethod, prevContact]);

  React.useEffect(() => {
    if (!expiresAt || !twoFactorSetupInProgress) {
      setDisplayTime('');
      return;
    }

    const updateTimer = () => {
      setDisplayTime(formatExpiresAt(expiresAt));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, twoFactorSetupInProgress]);

  React.useEffect(() => {
    if (!twoFactorSetupInProgress) {
      setPrevMethod(formData.twoFactorMethod);
      setPrevContact(
        formData.twoFactorMethod === 'phone' ? formData.twoFactorPhone : formData.twoFactorEmail
      );
    }
  }, [twoFactorSetupInProgress, formData.twoFactorMethod, formData.twoFactorPhone, formData.twoFactorEmail]);

  const isEditing = editingSection === 'twoStep';
  const displayMethod = formData.twoFactorMethod === 'phone' ? 'Phone' : 'Email';
  const displayValue =
    formData.twoFactorMethod === 'phone'
      ? maskValue(formData.twoFactorPhone)
      : maskValue(formData.twoFactorEmail);

  const handleDisableClick = () => {
    setShowDisableConfirmModal(true);
  };

  const handleDisableConfirm = () => {
    setShowDisableConfirmModal(false);
    onDisableTwoFactor?.();
  };

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 20, color: '#1890ff' }} />
          <CardTitleText level={4}>Two-Step Verification</CardTitleText>
        </CardTitle>
        <Tooltip
          title={
            formData.twoFactorEnabled
              ? 'Disable 2FA to edit settings'
              : profileLoading
              ? 'Loading...'
              : ''
          }
          placement="top"
        >
          <ActionIconButton
            onClick={profileLoading || formData.twoFactorEnabled ? undefined : onEdit}
            iconColor="#2D6CCA"
            style={{
              opacity: profileLoading || formData.twoFactorEnabled ? 0.5 : 1,
              cursor: profileLoading || formData.twoFactorEnabled ? 'not-allowed' : 'pointer'
            }}
          >
            <FontAwesomeIcon icon={faPen} fontSize={14} />
          </ActionIconButton>
        </Tooltip>
      </CardHeader>

      {!isEditing ? (
        <>
          <PreferenceRow>
            <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 16, color: '#1890ff' }} />
            <PreferenceInfo>
              <PreferenceLabel>Two-Step Verification</PreferenceLabel>
              <PreferenceDescription>
                Add an extra layer of security using your personal phone number or email
              </PreferenceDescription>
            </PreferenceInfo>
            <Badge
              status={formData.twoFactorEnabled ? 'success' : 'default'}
              text={formData.twoFactorEnabled ? 'Enabled' : 'Disabled'}
            />
          </PreferenceRow>
          {formData.twoFactorEnabled && (
            <>
              <PreferenceRow>
                <PreferenceInfo>
                  <PreferenceLabel>Verification method</PreferenceLabel>
                  <PreferenceDescription>
                    {displayMethod}: {displayValue}
                  </PreferenceDescription>
                </PreferenceInfo>
              </PreferenceRow>
              {onDisableTwoFactor && (
                <div style={{ marginTop: 16 }}>
                  <DisableButton
                    onClick={handleDisableClick}
                    loading={setupLoading}
                  >
                    Disable Two-Step Verification
                  </DisableButton>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <FormSection>
          <Form layout="vertical">
            <Form.Item label="Enable Two-Step Verification">
              <TwoStepEnableBlock>
                <PreferenceInfo>
                  <TwoStepEnableTitle>Two-Step Verification</TwoStepEnableTitle>
                  <TwoStepEnableDescription>
                    Verify your identity using a code sent to your personal phone or email
                  </TwoStepEnableDescription>
                </PreferenceInfo>
                <TwoStepSwitchWrap>
                  <StyledSwitch
                    checked={formData.twoFactorEnabled}
                    onChange={(checked) => onSwitchChange('twoFactorEnabled', checked)}
                  />
                </TwoStepSwitchWrap>
              </TwoStepEnableBlock>
            </Form.Item>

          {formData.twoFactorEnabled && (
            <>
              <Form.Item label="Verification method">
                <Radio.Group
                  value={formData.twoFactorMethod}
                  onChange={(e) => onMethodChange(e.target.value as TwoFactorMethod)}
                >
                  <TwoStepMethodOption value="phone">
                    <FontAwesomeIcon icon={faMobileScreen} />
                    <span>Personal phone number</span>
                  </TwoStepMethodOption>
                  <TwoStepMethodOption value="email">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <span>Personal email</span>
                  </TwoStepMethodOption>
                </Radio.Group>
              </Form.Item>

              {formData.twoFactorMethod === 'phone' && (
                <Form.Item label="Personal phone number">
                  <TwoStepInputWrap>
                    <StyledInput
                      name="twoFactorPhone"
                      value={formData.twoFactorPhone}
                      onChange={onInputChange}
                      placeholder="e.g. +1-416-555-1234 or 416-555-1234"
                      type="tel"
                      maxLength={20}
                      aria-label="Personal phone number for two-step verification"
                    />
                  </TwoStepInputWrap>
                </Form.Item>
              )}

              {formData.twoFactorMethod === 'email' && (
                <Form.Item label="Personal email">
                  <TwoStepInputWrap>
                    <StyledInput
                      name="twoFactorEmail"
                      value={formData.twoFactorEmail}
                      onChange={onInputChange}
                      placeholder="e.g. you@example.com"
                      type="email"
                      aria-label="Personal email for two-step verification"
                    />
                  </TwoStepInputWrap>
                </Form.Item>
              )}

              {twoFactorSetupInProgress && (
                <Form.Item
                  label="Verification Code"
                  extra={displayTime}
                >
                  <TwoStepInputWrap>
                    <StyledInput
                      name="twoFactorCode"
                      value={twoFactorCode || ''}
                      onChange={(e) => onTwoFactorCodeChange?.(e.target.value)}
                      placeholder="Enter 6-digit code"
                      type="text"
                      maxLength={6}
                      aria-label="Verification code for two-step authentication"
                    />
                  </TwoStepInputWrap>
                  {onResendTwoFactor && (
                    <div style={{ marginTop: 8, textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={onResendTwoFactor}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#2D6CCA',
                          cursor: 'pointer',
                          fontSize: 14,
                          textDecoration: 'underline',
                          padding: 0,
                        }}
                      >
                        Resend Code
                      </button>
                    </div>
                  )}
                </Form.Item>
              )}
            </>
          )}

          </Form>
          {formData.twoFactorEnabled && (
            <ActionButtons>
              {twoFactorSetupInProgress && hasContactChanged ? (
                <>
                  <SaveChangesButton
                    icon={<CheckOutlined />}
                    onClick={onSetupTwoFactor}
                    loading={setupLoading}
                  >
                    Send Verification Code
                  </SaveChangesButton>
                  <CancelButton onClick={onCancel}>Cancel</CancelButton>
                </>
              ) : twoFactorSetupInProgress && onConfirmTwoFactor ? (
                <>
                  <SaveChangesButton
                    icon={<CheckOutlined />}
                    onClick={onConfirmTwoFactor}
                    loading={setupLoading}
                    disabled={!formData.twoFactorEnabled}
                  >
                    Verify & Enable 2FA
                  </SaveChangesButton>
                  <CancelButton onClick={onCancel}>Cancel</CancelButton>
                </>
              ) : onSetupTwoFactor ? (
                <>
                  <SaveChangesButton
                    icon={<CheckOutlined />}
                    onClick={onSetupTwoFactor}
                    loading={setupLoading}
                  >
                    Send Verification Code
                  </SaveChangesButton>
                  <CancelButton onClick={onCancel}>Cancel</CancelButton>
                </>
              ) : null}
            </ActionButtons>
          )}
        </FormSection>
      )}

      <Modal
        title="Disable Two-Step Verification?"
        open={showDisableConfirmModal}
        onOk={handleDisableConfirm}
        onCancel={() => setShowDisableConfirmModal(false)}
        okText="Disable"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        centered
      >
        <p>
          Are you sure you want to disable Two-Step Verification? This will reduce the security of your account.
        </p>
        <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
          You will no longer need to enter a verification code when logging in.
        </p>
      </Modal>
    </SettingsCard>
  );
};

export default React.memo(TwoStepVerificationSection);
